/**
 * Chunked upload of a `File` into the sandbox workspace over AHP `resourceWrite`.
 *
 * Transport-agnostic on purpose: it takes a {@link SandboxWriter} rather than a relay, so
 * the chunking and collision logic is unit-testable without a socket, and so a future
 * blob-staging backend (upload to storage, have the host fetch a signed URL) can be
 * swapped in without touching any composer.
 *
 * ## Why writes are sequential
 *
 * Chunks of one file cannot be parallelized or even pipelined. AHP has no positional
 * overwrite that preserves the tail of a file:
 *
 * - `append` is rooted at EOF and is atomic against other appenders, but the protocol does
 *   not promise the host processes concurrent requests in send order — two in-flight
 *   appends can land swapped and silently corrupt the file.
 * - `truncate` at an offset yields `existing[0..position] + data`, discarding later chunks.
 * - `insert` splices and shifts, so it is equally order-dependent.
 *
 * So every chunk awaits the previous one. Across *files* concurrency is possible but
 * defaults to 1: the Web PubSub lane is a single socket, so concurrency interleaves rather
 * than adds throughput, and the transport's reassembly timeout is per chunk-group —
 * interleaving pushes both groups toward it.
 */

import {MAX_SANDBOX_UPLOAD_BYTES, MAX_SANDBOX_UPLOAD_TOTAL_BYTES, MAX_SANDBOX_UPLOADS_PER_TURN} from './upload-routing'
import {sanitizeUploadFilename, withCollisionSuffix} from './sanitize-upload-filename'

/**
 * Raw bytes per `resourceWrite` call, before base64 expands them by 4/3.
 *
 * 3 MiB of source becomes 4 MiB on the wire: ~4.6 frames at the transport's 900 KB
 * ceiling, far under its 32 MB per-group reassembly limit, and small enough to finish
 * inside the 30 s reassembly timeout on a slow connection. It is a multiple of 3 so each
 * chunk base64-encodes independently with no padding, which is what makes concatenating
 * the decoded chunks byte-exact.
 */
export const UPLOAD_CHUNK_BYTES = 3 * 1024 * 1024

/** Attempts at a collision-suffixed name before falling back to a unique id. */
const MAX_COLLISION_ATTEMPTS = 5

/** Automatic retries for a transient (non-denial) failure, per chunk. */
const MAX_TRANSIENT_RETRIES = 2

export interface SandboxWriteParams {
  uri: string
  data: string
  encoding: 'base64' | 'utf-8'
  contentType?: string
  createOnly?: boolean
  mode?: 'truncate' | 'append' | 'insert'
  position?: number
}

/**
 * The host-side operations the uploader needs. `write` must reject with an error carrying
 * `kind: 'alreadyExists'` when `createOnly` hits an existing file, and `kind: 'denied'`
 * when the host refuses the write — those two drive renaming and abort respectively.
 */
export interface SandboxWriter {
  write(params: SandboxWriteParams, signal?: AbortSignal): Promise<void>
  mkdir(uri: string): Promise<void>
  remove(uri: string): Promise<void>
  /** Builds a `file:` URI from the upload directory and a single filename segment. */
  resolve(filename: string): string
  /**
   * Stats the written file. Optional because older hosts may not implement
   * `resourceResolve`; when absent the client-built URI is used unverified.
   */
  stat?(uri: string): Promise<{uri: string; size?: number}>
}

export interface UploadProgress {
  /** Bytes confirmed written by the host. */
  uploadedBytes: number
  totalBytes: number
}

export interface UploadedFile {
  /** Final on-disk URI, after any collision renaming. */
  uri: string
  /** Final on-disk filename, which may differ from `file.name`. */
  filename: string
  contentType: string
  bytes: number
}

export class UploadAbortedError extends Error {
  constructor() {
    super('upload aborted')
    this.name = 'UploadAbortedError'
  }
}

interface KindedError {
  kind?: string
}

function failureKind(error: unknown): string | undefined {
  return typeof error === 'object' && error !== null ? (error as KindedError).kind : undefined
}

function isRetriable(error: unknown): boolean {
  const kind = failureKind(error)
  // An unknown (non-kinded) rejection is treated as transient: a dropped socket surfaces
  // that way, and a genuine permanent failure simply fails again on the retry.
  return kind === undefined || kind === 'failed'
}

/**
 * Encodes a byte slice as base64 without a `FileReader` round trip.
 *
 * Chunks are already bounded at {@link UPLOAD_CHUNK_BYTES}, so the loop is short-lived;
 * the 8 KB stride keeps `String.fromCharCode` from blowing the argument limit on large
 * slices. The whole-file path still uses `readFileAsBase64` for the prompt copy.
 */
export function encodeBytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const stride = 8192
  for (let offset = 0; offset < bytes.length; offset += stride) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + stride))
  }
  return btoa(binary)
}

export function resolveUploadContentType(file: File): string {
  return file.type || 'application/octet-stream'
}

/**
 * Number of `resourceWrite` calls a file of this size needs. Exposed for progress
 * estimation and for tests asserting the chunk boundary maths.
 */
export function chunkCountFor(size: number, chunkBytes: number = UPLOAD_CHUNK_BYTES): number {
  return size === 0 ? 1 : Math.ceil(size / chunkBytes)
}

export interface UploadFileOptions {
  writer: SandboxWriter
  file: File
  /** Seeds the fallback name when `file.name` sanitizes away entirely. */
  fallbackName: string
  chunkBytes?: number
  signal?: AbortSignal
  onProgress?: (progress: UploadProgress) => void
}

/**
 * Writes one file into the sandbox, chunk by chunk, resolving to its final on-disk path.
 *
 * The first chunk uses `truncate` + `createOnly` so an existing file is never overwritten;
 * on collision the whole upload restarts under the next candidate name. Later chunks
 * append at EOF.
 */
export async function uploadFileToSandbox({
  writer,
  file,
  fallbackName,
  chunkBytes = UPLOAD_CHUNK_BYTES,
  signal,
  onProgress,
}: UploadFileOptions): Promise<UploadedFile> {
  const contentType = resolveUploadContentType(file)
  const baseName = sanitizeUploadFilename(file.name, fallbackName)

  for (let attempt = 0; attempt <= MAX_COLLISION_ATTEMPTS; attempt++) {
    const filename =
      attempt > MAX_COLLISION_ATTEMPTS - 1
        ? sanitizeUploadFilename(`${fallbackName}-${baseName}`, fallbackName)
        : withCollisionSuffix(baseName, attempt)
    const uri = writer.resolve(filename)

    try {
      await writeAllChunks({writer, file, uri, contentType, chunkBytes, signal, onProgress})
      const confirmed = await confirmWrite(writer, uri, file.size)
      return {uri: confirmed, filename, contentType, bytes: file.size}
    } catch (error) {
      if (failureKind(error) !== 'alreadyExists') throw error
      // Only the first chunk can report `alreadyExists` (it is the only `createOnly`
      // write), so nothing has been written under this name and there is nothing to
      // clean up before trying the next candidate.
      onProgress?.({uploadedBytes: 0, totalBytes: file.size})
    }
  }

  throw new Error(`could not find an unused filename for ${file.name}`)
}

async function writeAllChunks({
  writer,
  file,
  uri,
  contentType,
  chunkBytes,
  signal,
  onProgress,
}: {
  writer: SandboxWriter
  file: File
  uri: string
  contentType: string
  chunkBytes: number
  signal?: AbortSignal
  onProgress?: (progress: UploadProgress) => void
}): Promise<void> {
  const total = chunkCountFor(file.size, chunkBytes)
  let uploadedBytes = 0
  let wroteFirstChunk = false

  try {
    for (let index = 0; index < total; index++) {
      throwIfAborted(signal)

      const start = index * chunkBytes
      // Slice the raw bytes, never the base64 string: base64 sliced at a non-multiple
      // of 4 decodes to different bytes, which is how chunked uploads silently corrupt
      // binaries.
      const slice = file.slice(start, Math.min(start + chunkBytes, file.size))
      const bytes = new Uint8Array(await slice.arrayBuffer())
      throwIfAborted(signal)

      await writeChunkWithRetry({
        writer,
        signal,
        params: {
          uri,
          data: encodeBytesToBase64(bytes),
          encoding: 'base64',
          contentType,
          ...(index === 0 ? {mode: 'truncate' as const, position: 0, createOnly: true} : {mode: 'append' as const}),
        },
        // A collision is only meaningful on the first chunk, where it means the name is
        // taken. Retrying it later would be a bug, so it is surfaced immediately.
        allowCollision: index === 0,
        uri,
        expectedSizeAfter: uploadedBytes + bytes.length,
      })

      wroteFirstChunk = true
      uploadedBytes += bytes.length
      onProgress?.({uploadedBytes, totalBytes: file.size})

      // Re-check after the write is acknowledged, not just before it. A signal that
      // aborts while the last chunk is in flight would otherwise let the loop finish
      // normally and leave the removed attachment's bytes on disk.
      throwIfAborted(signal)
    }
  } catch (error) {
    // A partial file is worse than no file: the agent could read it as complete. Leave
    // a name-collision alone, since that write never created anything.
    if (wroteFirstChunk && failureKind(error) !== 'alreadyExists') {
      try {
        await writer.remove(uri)
      } catch {
        // Best effort. The original failure is what the caller needs to see.
      }
    }
    throw error
  }
}

/**
 * Confirms the bytes landed and returns the canonical path the agent will see.
 *
 * `resourceWrite` returns an empty result, so without this the client can only assume
 * its own constructed URI is correct. Stating the file also catches a short write: the
 * chunks are appended one at a time, and a truncated file handed to the agent as a
 * complete one is worse than a failed upload.
 */
async function confirmWrite(writer: SandboxWriter, uri: string, expectedBytes: number): Promise<string> {
  if (!writer.stat) return uri

  const stat = await writer.stat(uri)
  if (stat.size !== undefined && stat.size !== expectedBytes) {
    throw new Error(`upload landed ${stat.size} bytes but expected ${expectedBytes}`)
  }
  return stat.uri || uri
}

async function writeChunkWithRetry({
  writer,
  params,
  signal,
  allowCollision,
  uri,
  expectedSizeAfter,
}: {
  writer: SandboxWriter
  params: SandboxWriteParams
  signal?: AbortSignal
  allowCollision: boolean
  uri: string
  expectedSizeAfter: number
}): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await writer.write(params, signal)
      return
    } catch (error) {
      if (allowCollision && failureKind(error) === 'alreadyExists') throw error
      if (attempt >= MAX_TRANSIENT_RETRIES || !isRetriable(error)) throw error
      throwIfAborted(signal)

      // A failed write is ambiguous: the host may have committed it and lost the
      // acknowledgement. `truncate` is idempotent so it can just be resent, but
      // resending an `append` would write the chunk twice and silently corrupt the
      // file, so establish what actually landed before retrying.
      if (params.mode === 'append') {
        const landed = await appendLanded({writer, uri, expectedSizeAfter})
        if (landed === 'yes') return
        if (landed === 'unknown') throw error
      }

      await delay(2 ** attempt * 250, signal)
    }
  }
}

/**
 * Resolves a partially written upload to decide whether an unacknowledged `append`
 * reached disk.
 *
 * Returns `unknown` when the host exposes no way to resolve the file, in which case the
 * caller must fail rather than guess: a duplicated chunk produces a corrupt file that
 * still reports success, which is worse than a failed upload the user can retry.
 */
async function appendLanded({
  writer,
  uri,
  expectedSizeAfter,
}: {
  writer: SandboxWriter
  uri: string
  expectedSizeAfter: number
}): Promise<'yes' | 'no' | 'unknown'> {
  if (!writer.stat) return 'unknown'
  try {
    const {size} = await writer.stat(uri)
    if (size === undefined) return 'unknown'
    if (size === expectedSizeAfter) return 'yes'
    if (size < expectedSizeAfter) return 'no'
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new UploadAbortedError()
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    function onAbort() {
      clearTimeout(timer)
      reject(new UploadAbortedError())
    }
    signal?.addEventListener('abort', onAbort, {once: true})
  })
}

export type UploadRejectionReason = 'too-large' | 'too-many' | 'total-too-large'

export interface UploadBudget {
  files: number
  bytes: number
}

/**
 * Whether a file fits the per-turn upload budget, given what is already queued.
 * Kept separate from {@link uploadFileToSandbox} so callers can reject before reading
 * any bytes.
 */
export function checkUploadBudget(size: number, used: UploadBudget): UploadRejectionReason | null {
  if (size > MAX_SANDBOX_UPLOAD_BYTES) return 'too-large'
  if (used.files >= MAX_SANDBOX_UPLOADS_PER_TURN) return 'too-many'
  if (used.bytes + size > MAX_SANDBOX_UPLOAD_TOTAL_BYTES) return 'total-too-large'
  return null
}
